import React from 'react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, FileText, Download, Mail, PenLine, Plus, X } from 'lucide-react';

import DocumentRectificationModal from '../DocumentRectificationModal';

export default function DOSDialogs({ ctx }) {
  const { loading: loading, pdfPreview, sendEmail, setSendEmail, rectifyModal, setRectifyModal, nomSign, setNomSign, isSuperAdmin, closePdfPreview, downloadFromPreview, submitSendEmail, submitNomSign, getAuthHeader } = ctx;
  return (
    <>
      <Dialog open={pdfPreview.open} onOpenChange={(open) => { if (!open) closePdfPreview(); }}>
        <DialogContent
          className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col gap-0"
          data-testid="pdf-preview-modal"
        >
          <DialogHeader className="px-5 py-3 border-b flex-row items-center justify-between space-y-0">
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileText className="h-5 w-5 text-violet-600" />
              {pdfPreview.type === 'pack'
                ? 'Aperçu Pack complet (Attestation + CP + CG + Note)'
                : pdfPreview.type === 'cpcg'
                ? 'Aperçu Dossier Maître (CP + CG + Note)'
                : pdfPreview.type === 'nominative'
                ? 'Aperçu Attestation nominative officielle (QR de vérification)'
                : `Aperçu Attestation ${pdfPreview.type === 'signed' ? 'signée' : 'DRAFT'}`}
              {pdfPreview.reference && (
                <span className="text-xs font-mono text-gray-500 ml-2">{pdfPreview.reference}</span>
              )}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={downloadFromPreview}
                disabled={!pdfPreview.url}
                className="bg-violet-600 hover:bg-violet-700 text-white h-8"
                data-testid="pdf-preview-download-btn"
              >
                <Download className="h-4 w-4 mr-1" /> Télécharger
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 bg-gray-100 relative overflow-hidden">
            {pdfPreview.loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
                <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
                <span className="ml-3 text-sm text-gray-700">Génération du PDF en cours…</span>
              </div>
            )}
            {pdfPreview.url && (
              <iframe
                key={pdfPreview.url}
                src={pdfPreview.url}
                title="Aperçu Attestation PDF"
                className="w-full h-full border-0"
                data-testid="pdf-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Envoyer Pack complet au Maître d'ouvrage */}
      <Dialog open={sendEmail.open} onOpenChange={(open) => { if (!open && !sendEmail.sending) setSendEmail(prev => ({ ...prev, open: false })); }}>
        <DialogContent className="sm:max-w-[520px]" data-testid="send-mo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-emerald-600" />
              Envoyer le Pack complet au Maître d'ouvrage
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-xs text-gray-600 bg-emerald-50 border-l-4 border-emerald-500 px-3 py-2 rounded">
              Le destinataire recevra un email Brevo avec un lien sécurisé (expiration 7 jours) donnant accès au dossier contractuel complet (Attestation + CP + CG + Note).
              Chaque consultation du lien est tracée dans l'audit trail.
            </div>
            <div className="space-y-1">
              <Label htmlFor="mo-email" className="text-sm">Email du Maître d'ouvrage <span className="text-red-500">*</span></Label>
              <Input
                id="mo-email"
                type="email"
                placeholder="mo@exemple.fr"
                value={sendEmail.to_email}
                onChange={(e) => setSendEmail(prev => ({ ...prev, to_email: e.target.value }))}
                data-testid="send-mo-email-input"
                disabled={sendEmail.sending}
              />
            </div>
            {isSuperAdmin && (
              <div className="space-y-2" data-testid="send-mo-cc-section">
                <Label className="text-sm">Destinataires supplémentaires (optionnel)</Label>
                {(sendEmail.cc_emails || []).map((em, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      type="email"
                      placeholder="autre.destinataire@exemple.fr"
                      value={em}
                      onChange={(e) => setSendEmail(prev => {
                        const cc = [...(prev.cc_emails || [])];
                        cc[i] = e.target.value;
                        return { ...prev, cc_emails: cc };
                      })}
                      data-testid={`send-mo-cc-input-${i}`}
                      disabled={sendEmail.sending}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 shrink-0"
                      onClick={() => setSendEmail(prev => ({ ...prev, cc_emails: (prev.cc_emails || []).filter((_, j) => j !== i) }))}
                      data-testid={`send-mo-cc-remove-${i}`}
                      disabled={sendEmail.sending}
                      title="Retirer ce destinataire"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSendEmail(prev => ({ ...prev, cc_emails: [...(prev.cc_emails || []), ''] }))}
                  className="text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
                  data-testid="send-mo-add-cc-btn"
                  disabled={sendEmail.sending}
                >
                  <Plus className="h-3 w-3 mr-1" /> Ajouter un destinataire
                </Button>
                <p className="text-[11px] text-gray-400">
                  Chaque destinataire reçoit son propre email avec un lien sécurisé personnel (7 jours), tracé dans l'audit trail.
                </p>
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="mo-name" className="text-sm">Nom du destinataire (optionnel)</Label>
              <Input
                id="mo-name"
                placeholder="M. / Mme / SCI ..."
                value={sendEmail.to_name}
                onChange={(e) => setSendEmail(prev => ({ ...prev, to_name: e.target.value }))}
                data-testid="send-mo-name-input"
                disabled={sendEmail.sending}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="mo-msg" className="text-sm">Message personnalisé (optionnel)</Label>
              <Textarea
                id="mo-msg"
                rows={3}
                placeholder="Mot d'accompagnement pour le Maître d'ouvrage..."
                value={sendEmail.message}
                onChange={(e) => setSendEmail(prev => ({ ...prev, message: e.target.value }))}
                data-testid="send-mo-message-input"
                disabled={sendEmail.sending}
              />
            </div>
            <div className="text-xs text-gray-500">
              Référence : <span className="font-mono">{sendEmail.reference}</span>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSendEmail(prev => ({ ...prev, open: false }))}
              disabled={sendEmail.sending}
              data-testid="send-mo-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={submitSendEmail}
              disabled={!sendEmail.to_email || sendEmail.sending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="send-mo-submit-btn"
            >
              {sendEmail.sending ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Envoi en cours...</>) : (<><Mail className="h-4 w-4 mr-2" />Envoyer l'email</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* V37.92 — Dialog Envoyer l'attestation nominative pour signature électronique au MO */}
      <Dialog open={nomSign.open} onOpenChange={(open) => { if (!open && !nomSign.sending) setNomSign(prev => ({ ...prev, open: false })); }}>
        <DialogContent className="sm:max-w-[520px]" data-testid="nom-sign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="h-5 w-5 text-teal-600" />
              Signature électronique de l'attestation nominative
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-xs text-gray-600 bg-teal-50 border-l-4 border-teal-500 px-3 py-2 rounded">
              Le Maître d'ouvrage recevra un email avec un lien sécurisé (validité 7 jours) lui permettant de
              consulter l'attestation nominative puis de la <strong>signer électroniquement</strong> après
              vérification par un code à usage unique envoyé par email. La signature est horodatée et tracée
              dans le registre d'audit du dossier.
            </div>
            <div className="space-y-1">
              <Label htmlFor="nom-sign-email" className="text-sm">Email du Maître d'ouvrage <span className="text-red-500">*</span></Label>
              <Input
                id="nom-sign-email"
                type="email"
                placeholder="mo@exemple.fr"
                value={nomSign.to_email}
                onChange={(e) => setNomSign(prev => ({ ...prev, to_email: e.target.value }))}
                data-testid="nom-sign-email-input"
                disabled={nomSign.sending}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="nom-sign-msg" className="text-sm">Message personnalisé (optionnel)</Label>
              <Textarea
                id="nom-sign-msg"
                rows={3}
                placeholder="Mot d'accompagnement pour le Maître d'ouvrage..."
                value={nomSign.message}
                onChange={(e) => setNomSign(prev => ({ ...prev, message: e.target.value }))}
                data-testid="nom-sign-message-input"
                disabled={nomSign.sending}
              />
            </div>
            <div className="text-xs text-gray-500">
              Référence : <span className="font-mono">{nomSign.reference}</span>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNomSign(prev => ({ ...prev, open: false }))}
              disabled={nomSign.sending}
              data-testid="nom-sign-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={submitNomSign}
              disabled={!nomSign.to_email || nomSign.sending}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="nom-sign-submit-btn"
            >
              {nomSign.sending ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Envoi en cours...</>) : (<><PenLine className="h-4 w-4 mr-2" />Envoyer pour signature</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale Rectification document signe (Super Admin uniquement) */}
      {isSuperAdmin && (
        <DocumentRectificationModal
          open={rectifyModal.open}
          onClose={() => setRectifyModal({ open: false, reference: null })}
          reference={rectifyModal.reference}
          getAuthHeader={getAuthHeader}
          domain="domium"
        />
      )}
    </>
  );
}
